import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError, PayoutType } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

// Leaderboard interface matching database
interface Leaderboard {
  name: string;
  description: string;
}

// LeaderboardPayoutSettings interface matching database
interface LeaderboardPayoutSetting {
  placement: number;
  amount: number;
  type: PayoutType;
  item_desc?: string; // only if type is 'item'
}

const LeaderboardStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  const [newLeaderboard, setNewLeaderboard] = useState<Leaderboard>({
    name: 'Most Points',
    description: t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.mostPoints')
  });
  const [selectedDivision, setSelectedDivision] = useState<string>('overall');

  const [selectedLeaderboardIndex, setSelectedLeaderboardIndex] = useState<number | null>(null);
  const [newPayoutSetting, setNewPayoutSetting] = useState<LeaderboardPayoutSetting>({
    placement: 1,
    amount: 0,
    type: 'money',
    item_desc: ''
  });

  const handleAddLeaderboard = () => {
    if (!newLeaderboard.name || !newLeaderboard.description) {
      addError('leaderboards', 'Please fill in all leaderboard fields');
      return;
    }

    const leaderboards = data.leaderboards || [];
    const leaderboardToAdd = {
      ...newLeaderboard,
      payoutSettings: [] as LeaderboardPayoutSetting[]
    };

    updateData({
      leaderboards: [...leaderboards, leaderboardToAdd]
    });

    setNewLeaderboard({ 
      name: 'Most Points', 
      description: t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.mostPoints')
    });
    setSelectedDivision('overall');
    clearErrors();
  };

  const handleAddPayoutSetting = () => {
    if (selectedLeaderboardIndex === null) {
      addError('payoutSettings', 'Please select a leaderboard first');
      return;
    }

    if (newPayoutSetting.type === 'money' && newPayoutSetting.amount <= 0) {
      addError('payoutSettings', 'Amount must be greater than 0 for money payouts');
      return;
    }

    if (newPayoutSetting.type === 'item' && !newPayoutSetting.item_desc) {
      addError('payoutSettings', 'Item description is required for item type payouts');
      return;
    }

    const leaderboards = [...(data.leaderboards || [])];
    if (!leaderboards[selectedLeaderboardIndex].payoutSettings) {
      leaderboards[selectedLeaderboardIndex].payoutSettings = [];
    }

    leaderboards[selectedLeaderboardIndex].payoutSettings!.push({ ...newPayoutSetting });

    // Sort by placement
    leaderboards[selectedLeaderboardIndex].payoutSettings!.sort((a, b) => a.placement - b.placement);

    updateData({ leaderboards });

    setNewPayoutSetting({
      placement: 1,
      amount: 0,
      type: 'money',
      item_desc: ''
    });
    clearErrors();
  };

  const handleRemoveLeaderboard = (index: number) => {
    const leaderboards = data.leaderboards || [];
    const updatedLeaderboards = leaderboards.filter((_, i) => i !== index);
    updateData({ leaderboards: updatedLeaderboards });
    if (selectedLeaderboardIndex === index) {
      setSelectedLeaderboardIndex(null);
    }
  };

  const handleRemovePayoutSetting = (leaderboardIndex: number, payoutIndex: number) => {
    const leaderboards = [...(data.leaderboards || [])];
    leaderboards[leaderboardIndex].payoutSettings!.splice(payoutIndex, 1);
    updateData({ leaderboards });
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.leaderboardStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.leaderboardStep.subtitle')}
        </p>
      </div>

      {/* Existing Leaderboards */}
      {data.leaderboards && data.leaderboards.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('admin.createLeague.steps.leaderboardStep.currentLeaderboards')}</h3>
          {data.leaderboards.map((leaderboard, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold text-white">{leaderboard.name}</h4>
                  <p className="text-purple-200 text-sm mt-1">{leaderboard.description}</p>

                  {leaderboard.payoutSettings && leaderboard.payoutSettings.length > 0 && (
                    <div className="mt-3">
                      <h5 className="text-sm font-medium text-purple-100 mb-2">{t('admin.createLeague.steps.leaderboardStep.payoutStructure')}:</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {leaderboard.payoutSettings.map((payout, payoutIndex) => (
                          <div key={payoutIndex} className="bg-white/10 rounded p-2 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-purple-200">
                                {payout.placement === 1 ? t('admin.createLeague.steps.leaderboardStep.placements.first') :
                                  payout.placement === 2 ? t('admin.createLeague.steps.leaderboardStep.placements.second') :
                                    payout.placement === 3 ? t('admin.createLeague.steps.leaderboardStep.placements.third') :
                                      t('admin.createLeague.steps.leaderboardStep.placements.nth', { n: payout.placement })}:
                                {payout.type === 'money' ? ` $${payout.amount}` : ` ${payout.item_desc}`}
                              </span>
                              <button
                                onClick={() => handleRemovePayoutSetting(index, payoutIndex)}
                                className="text-red-400 hover:text-red-300 ml-2"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setSelectedLeaderboardIndex(index)}
                    className={`px-3 py-1 text-sm rounded ${selectedLeaderboardIndex === index
                        ? 'bg-green-600/50 text-white'
                        : 'bg-green-600/20 text-green-200 hover:bg-green-600/40'
                      }`}
                  >
                    {selectedLeaderboardIndex === index ? t('admin.createLeague.steps.leaderboardStep.selected') : t('admin.createLeague.steps.leaderboardStep.selectToSetPayouts')}
                  </button>
                  <button
                    onClick={() => handleRemoveLeaderboard(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    {t('admin.createLeague.steps.leaderboardStep.remove')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

       {/* Add Payout Settings */}
      {data.leaderboards && data.leaderboards.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.leaderboardStep.addPayoutSettings')}</h3>

          {selectedLeaderboardIndex === null ? (
            <p className="text-purple-200 text-center py-4">
              {t('admin.createLeague.steps.leaderboardStep.selectLeaderboardPrompt')}
            </p>
          ) : (
            <>
              <div className="mb-4 p-3 bg-blue-500/20 border border-blue-400 rounded-lg">
                <p className="text-blue-200 text-sm">
                  {t('admin.createLeague.steps.leaderboardStep.configuringPayoutsFor')}: <strong>{data.leaderboards[selectedLeaderboardIndex].name}</strong>
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.createLeague.steps.leaderboardStep.placement')} *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newPayoutSetting.placement}
                    onChange={(e) => setNewPayoutSetting({ ...newPayoutSetting, placement: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.createLeague.steps.leaderboardStep.payoutType')} *
                  </label>
                  <select
                    value={newPayoutSetting.type}
                    onChange={(e) => setNewPayoutSetting({ ...newPayoutSetting, type: e.target.value as PayoutType })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="money" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.payoutTypes.money')}</option>
                    <option value="item" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.payoutTypes.item')}</option>
                  </select>
                </div>

                {newPayoutSetting.type === 'money' ? (
                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      {t('admin.createLeague.steps.leaderboardStep.amount')} *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newPayoutSetting.amount}
                      onChange={(e) => setNewPayoutSetting({ ...newPayoutSetting, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      {t('admin.createLeague.steps.leaderboardStep.itemDescription')} *
                    </label>
                    <input
                      type="text"
                      value={newPayoutSetting.item_desc || ''}
                      onChange={(e) => setNewPayoutSetting({ ...newPayoutSetting, item_desc: e.target.value, amount: 0 })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder={t('admin.createLeague.steps.leaderboardStep.itemPlaceholder')}
                    />
                  </div>
                )}
              </div>

              <button
                onClick={handleAddPayoutSetting}
                className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('admin.createLeague.steps.leaderboardStep.addPayoutSetting')}
              </button>

              {getFieldError('payoutSettings') && (
                <p className="text-red-400 text-sm mt-2">{getFieldError('payoutSettings')}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Add New Leaderboard */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.leaderboardStep.addNewLeaderboard')}</h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.leaderboardStep.leaderboardName')} *
            </label>
            <select value={newLeaderboard.name} onChange={(e) => {
              const selectedName = e.target.value;
              let defaultDescription = '';
              
              // Set default descriptions based on leaderboard type
              if (selectedName === 'Most Points') {
                defaultDescription = t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.mostPoints');
              } else if (selectedName === 'Overall Pinfall') {
                defaultDescription = t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.overall'); // Will be updated when division is selected
              } else if (selectedName === 'Overall Team Pinfall') {
                defaultDescription = t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.overallTeamPinfall');
              }
              
              setNewLeaderboard({ ...newLeaderboard, name: selectedName, description: defaultDescription });
              // Reset division when changing leaderboard type
              setSelectedDivision('overall');
            }}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}>
              <option value="Most Points" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.mostPoints')}</option>
              <option value="Overall Pinfall" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.overallPinfall')}</option>
              <option value="Overall Team Pinfall" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.overallTeamPinfall')}</option>
            </select>
          </div>

          {/* Show division selector only for Overall Pinfall */}
          {newLeaderboard.name === 'Overall Pinfall' && (
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.leaderboardStep.division')} *
              </label>
              <select 
                value={selectedDivision} 
                onChange={(e) => {
                  const division = e.target.value;
                  setSelectedDivision(division);
                  // Auto-fill description with division
                  const divisionText = division === 'overall' 
                    ? t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.overall')
                    : t('admin.createLeague.steps.leaderboardStep.defaultDescriptions.division', { division: division.toUpperCase() });
                  setNewLeaderboard({ 
                    ...newLeaderboard, 
                    description: divisionText
                  });
                }}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}>
                <option value="overall" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.overall')}</option>
                <option value="a" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.a')}</option>
                <option value="b" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.b')}</option>
                <option value="c" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.c')}</option>
                <option value="d" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.d')}</option>
                <option value="e" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.divisions.e')}</option>
              </select>
            </div>
          )}

          <div className={newLeaderboard.name === 'Overall Pinfall' ? 'md:col-span-2' : ''}>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.leaderboardStep.description')} *
            </label>
            <input
              type="text"
              value={newLeaderboard.description}
              onChange={(e) => setNewLeaderboard({ ...newLeaderboard, description: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={newLeaderboard.name === 'Overall Pinfall' ? t('admin.createLeague.steps.leaderboardStep.autoFilledPlaceholder') : t('admin.createLeague.steps.leaderboardStep.descriptionPlaceholder')}
              readOnly={newLeaderboard.name === 'Overall Pinfall'}
            />
          </div>
        </div>

        <button
          onClick={handleAddLeaderboard}
          className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
        >
          {t('admin.createLeague.steps.leaderboardStep.addLeaderboard')}
        </button>

        {getFieldError('leaderboards') && (
          <p className="text-red-400 text-sm mt-2">{getFieldError('leaderboards')}</p>
        )}
      </div>

     

      {/* Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.leaderboardStep.examples.title')}</h3>
        <div className="grid md:grid-cols-2 gap-4 text-purple-200 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.leaderboardStep.examples.performance.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.leaderboardStep.examples.performance.mostPoints')}</li>
              <li>• {t('admin.createLeague.steps.leaderboardStep.examples.performance.overallPinfall')}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.leaderboardStep.examples.other.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.leaderboardStep.examples.other.configureNext')}</li>
              <li>• {t('admin.createLeague.steps.leaderboardStep.examples.other.setPayouts')}</li>
            </ul>
          </div>
        </div>
      </div>

      {(!data.leaderboards || data.leaderboards.length === 0) && (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 text-center">
          <p className="text-yellow-200">
            {t('admin.createLeague.steps.leaderboardStep.noLeaderboardsWarning')}
          </p>
        </div>
      )}
    </div>
  );
};

export default LeaderboardStep;